// ===========================================
// Teacher Performance Table Component
// ===========================================
// Table showing detailed teacher performance data with sorting and pagination

import { useState } from 'react';
import { describeTaskCounts } from './TeacherPerformanceSummaryCards';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
  Pagination,
  TableSortLabel,
} from '@mui/material';

import { TeacherPerformanceRecord } from '../../api/reports.api';
import { formatPercentage } from '../../hooks/useReports';

// ===========================================
// TYPES
// ===========================================

interface TeacherPerformanceTableProps {
  teachers: TeacherPerformanceRecord[];
  loading?: boolean;
}

type SortField = 'teacherName' | 'lessonsDelivered' | 'averageAttendanceRate' | 'uniqueStudents';
type SortOrder = 'asc' | 'desc';

// ===========================================
// COMPONENT
// ===========================================

export default function TeacherPerformanceTable({
  teachers,
  loading = false,
}: TeacherPerformanceTableProps) {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('lessonsDelivered');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const rowsPerPage = 25;

  // Sort teachers
  const sortedTeachers = [...teachers].sort((a, b) => {
    let aValue: any = a[sortField];
    let bValue: any = b[sortField];

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Paginate
  const paginatedTeachers = sortedTeachers.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );
  const pageCount = Math.ceil(teachers.length / rowsPerPage);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const getAttendanceColor = (rate: number): 'success' | 'warning' | 'error' => {
    if (rate >= 90) return 'success';
    if (rate >= 75) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Loading teacher performance data...
        </Typography>
      </Box>
    );
  }

  if (teachers.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No teacher performance data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'teacherName'}
                  direction={sortField === 'teacherName' ? sortOrder : 'asc'}
                  onClick={() => handleSort('teacherName')}
                >
                  Teacher
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'lessonsDelivered'}
                  direction={sortField === 'lessonsDelivered' ? sortOrder : 'asc'}
                  onClick={() => handleSort('lessonsDelivered')}
                >
                  Lessons
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Attendance</TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'averageAttendanceRate'}
                  direction={sortField === 'averageAttendanceRate' ? sortOrder : 'asc'}
                  onClick={() => handleSort('averageAttendanceRate')}
                >
                  Avg Rate
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'uniqueStudents'}
                  direction={sortField === 'uniqueStudents' ? sortOrder : 'asc'}
                  onClick={() => handleSort('uniqueStudents')}
                >
                  Students
                </TableSortLabel>
              </TableCell>
              <TableCell>Instruments</TableCell>
              <TableCell align="right">Tasks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTeachers.map((teacher) => (
              <TableRow key={teacher.teacherId} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {teacher.teacherName}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{teacher.lessonsDelivered}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {teacher.totalAttendanceRecords} records
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={formatPercentage(teacher.averageAttendanceRate)}
                    size="small"
                    color={getAttendanceColor(teacher.averageAttendanceRate)}
                    sx={{ minWidth: 60 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{teacher.uniqueStudents}</Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {teacher.instruments.map((instrument, index) => (
                      <Chip
                        key={index}
                        label={instrument}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={teacher.outstandingTasks?.total ?? 0}
                    size="small"
                    color={(teacher.outstandingTasks?.total ?? 0) > 0 ? 'error' : 'success'}
                    variant={(teacher.outstandingTasks?.total ?? 0) > 0 ? 'filled' : 'outlined'}
                    title={describeTaskCounts(teacher.outstandingTasks)}
                    sx={{ minWidth: 40 }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Summary Footer */}
      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Typography variant="body2" color="text.secondary">
          Showing {paginatedTeachers.length} of {teachers.length} teachers
        </Typography>
      </Box>
    </Box>
  );
}
